import { imageFormatRegistry } from '../format-registry';
import { DecodedImage, IImageFormat } from '../format-interface';
import { serializeProject } from './serializer';

/**
 * @file project/exporter.ts
 * @description Logic for exporting project data into various image formats.
 * Following the stateless architecture, this module relies strictly on input parameters.
 */

/**
 * @function exportProjectToFormat
 * @description Flattens the project layers and encodes the image into the requested binary format.
 * This is a pure function that does not access global state.
 * @param {number} width - The total image width.
 * @param {number} height - The total image height.
 * @param {any[]} layers - Array of project layers.
 * @param {string} formatExtension - The target file extension (e.g., '.png', '.psd').
 * @returns {Promise<Uint8Array | string>} The encoded file data.
 */
export async function exportProjectToFormat(
  width: number,
  height: number,
  layers: any[],
  formatExtension: string
): Promise<Uint8Array | string> {
  const format: IImageFormat | null = imageFormatRegistry.getByExtension(formatExtension);
  
  // Handle native HCIE project format
  if (formatExtension === ".hcie" || !format || !format.canWrite) {
    // Background color is hardcoded to white for legacy project saves if not provided
    return await serializeProject(width, height, "#ffffff", layers);
  }

  // [CRITICAL_LOGIC_START]
  // STEP 1: Flattening visible layers for composite preview
  const compositeCanvas: HTMLCanvasElement = document.createElement("canvas");
  compositeCanvas.width = width;
  compositeCanvas.height = height;
  const context: CanvasRenderingContext2D | null = compositeCanvas.getContext("2d");
  
  if (!context) {
    throw new Error("[Exporter] Failed to create 2D context for image flattening.");
  }

  for (const layer of layers) {
    if (layer.visible) {
      context.save();
      context.globalAlpha = layer.opacity;
      context.globalCompositeOperation = (layer.blendMode as GlobalCompositeOperation) || "source-over";
      
      const layerCanvas: any = layer.canvas;
      context.drawImage(layerCanvas, layer.x || 0, layer.y || 0);
      context.restore();
    }
  }

  const flattenedImageData: ImageData = context.getImageData(0, 0, width, height);
  // [CRITICAL_LOGIC_END]

  // STEP 2: Building DecodedImage structure for adapters
  const decoded: DecodedImage = {
    width: width,
    height: height,
    composite: flattenedImageData,
    layers: layers.map(layer => ({
      name: layer.name,
      canvas: layer.canvas,
      visible: layer.visible,
      opacity: layer.opacity,
      blendMode: layer.blendMode,
      x: layer.x || 0,
      y: layer.y || 0
    }))
  };

  const buffer: ArrayBuffer = await format.write(decoded);
  return new Uint8Array(buffer);
}
